import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { EventBus, type AttachmentDTO } from '@campus/core';
import { Button, Icon } from '../ds/index.js';
import {
  useMessagingStore, attachmentBlobUrl, attachmentFileUrl, removeDraftFile, retryDraftFile, type DraftFile,
} from '../../messaging/store.js';
import { formatDuration, formatSize, uploadErrorLabel } from './format.js';
import { VoicePlayer } from './Voice.js';

/**
 * Fichiers d'un message (tasks/messagerie-plan.md, § 5) : photos en vignettes
 * (agrandies au clic), vidéos et sons lus sur place, documents à télécharger.
 * Aperçus chargés avec le jeton en en-tête ; vidéo, son et téléchargement par
 * une adresse signée (une balise <video> n'envoie pas d'en-tête).
 */

const frame = {
  borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border-default)', background: 'var(--color-bg-raised)', overflow: 'hidden',
} as const;

function useBlobUrl(id: string, variant: 'original' | 'preview', enabled = true): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    void attachmentBlobUrl(id, variant).then((u) => { if (alive) setUrl(u); });
    return () => { alive = false; };
  }, [id, variant, enabled]);
  return url;
}

function Lightbox({ attachment, onClose }: { attachment: AttachmentDTO; onClose: () => void }) {
  const full = useBlobUrl(attachment.id, 'original');
  const preview = useBlobUrl(attachment.id, 'preview');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return createPortal(
    <div
      role="dialog" aria-label={attachment.name} onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(8, 10, 20, 0.88)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24 }}
    >
      <img
        src={full ?? preview ?? undefined} alt={attachment.name} onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '100%', maxHeight: 'calc(100dvh - 120px)', objectFit: 'contain', borderRadius: 'var(--radius-md)' }}
      />
      <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
        <Button variant="secondary" icon="download" onClick={() => { void downloadAttachment(attachment); }}>Télécharger</Button>
        <Button variant="secondary" icon="x" onClick={onClose}>Fermer</Button>
      </div>
    </div>,
    document.body,
  );
}

async function downloadAttachment(a: AttachmentDTO) {
  try {
    const url = await attachmentFileUrl(a.id, true);
    const link = document.createElement('a');
    link.href = url;
    link.download = a.name;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch {
    EventBus.emit('ui:toast', { message: 'Le fichier n’a pas pu être ouvert. Réessaie.', tone: 'warning' });
  }
}

/** GIF ou WebP : peut être animé, alors que l'aperçu n'en garde que la première image. */
const mayBeAnimated = (a: AttachmentDTO) => (a.mime === 'image/gif' || a.mime === 'image/webp') && a.size <= 8 * 1024 * 1024;

function ImageTile({ attachment, single }: { attachment: AttachmentDTO; single: boolean }) {
  const url = useBlobUrl(attachment.id, attachment.hasPreview && !mayBeAnimated(attachment) ? 'preview' : 'original');
  const [open, setOpen] = useState(false);
  const ratio = attachment.width && attachment.height ? attachment.width / attachment.height : 4 / 3;
  return (
    <>
      <button
        type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }} aria-label={`Agrandir ${attachment.name}`}
        style={{ ...frame, padding: 0, cursor: 'zoom-in', display: 'block', width: '100%', aspectRatio: single ? String(Math.max(0.6, Math.min(ratio, 2))) : '1' }}
      >
        {url && <img src={url} alt={attachment.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
      </button>
      {open && <Lightbox attachment={attachment} onClose={() => setOpen(false)} />}
    </>
  );
}

function VideoTile({ attachment }: { attachment: AttachmentDTO }) {
  const poster = useBlobUrl(attachment.id, 'preview', attachment.hasPreview);
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const play = async () => {
    try { setSrc(await attachmentFileUrl(attachment.id)); } catch { setFailed(true); }
  };
  const ratio = attachment.width && attachment.height ? attachment.width / attachment.height : 16 / 9;
  return (
    <div style={{ ...frame, position: 'relative', aspectRatio: String(Math.max(0.6, Math.min(ratio, 2))) }} onClick={(e) => e.stopPropagation()}>
      {src ? (
        <video
          src={src} poster={poster ?? undefined} controls autoPlay playsInline preload="metadata"
          onError={() => setFailed(true)}
          style={{ width: '100%', height: '100%', display: 'block', background: '#000' }}
        />
      ) : (
        <button
          type="button" onClick={() => { void play(); }} aria-label={`Lire la vidéo ${attachment.name}`}
          style={{ all: 'unset', cursor: 'pointer', position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: poster ? `center / cover no-repeat url(${poster})` : 'var(--color-bg-raised)' }}
        >
          <span style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(8, 10, 20, 0.7)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="play" size={22} />
          </span>
          {attachment.durationMs && (
            <span className="pl-num" style={{ position: 'absolute', right: 8, bottom: 8, padding: '2px 6px', borderRadius: 6, background: 'rgba(8, 10, 20, 0.7)', color: '#fff', fontSize: 'var(--text-xs)' }}>
              {formatDuration(attachment.durationMs)}
            </span>
          )}
        </button>
      )}
      {failed && (
        <div role="alert" style={{ position: 'absolute', left: 8, right: 8, bottom: 8, display: 'flex', gap: 8, alignItems: 'center', padding: 8, borderRadius: 'var(--radius-sm)', background: 'var(--color-bg-surface)' }}>
          <span className="pl-msg-meta" style={{ flex: 1 }}>Ce navigateur ne lit pas cette vidéo.</span>
          <Button size="sm" variant="secondary" icon="download" onClick={() => { void downloadAttachment(attachment); }}>Télécharger</Button>
        </div>
      )}
    </div>
  );
}

function Waveform({ values }: { values: number[] }) {
  return (
    <span aria-hidden="true" style={{ display: 'flex', alignItems: 'center', gap: 1, height: 24, flex: 1, minWidth: 0 }}>
      {values.map((v, i) => (
        <span key={i} style={{ flex: 1, minWidth: 1, height: `${Math.max(8, (v / 255) * 100)}%`, borderRadius: 1, background: 'var(--color-primary-text)', opacity: 0.7 }} />
      ))}
    </span>
  );
}

function AudioTile({ attachment }: { attachment: AttachmentDTO }) {
  const [src, setSrc] = useState<string | null>(null);
  const load = async () => {
    try { setSrc(await attachmentFileUrl(attachment.id)); } catch {
      EventBus.emit('ui:toast', { message: 'Ce son n’a pas pu être chargé. Réessaie.', tone: 'warning' });
    }
  };
  return (
    <div style={{ ...frame, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Icon name="music" size={18} />
        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--text-sm)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{attachment.name}</span>
        {attachment.durationMs && <span className="pl-msg-meta pl-num">{formatDuration(attachment.durationMs)}</span>}
      </div>
      {src ? (
        <audio src={src} controls autoPlay preload="metadata" style={{ width: '100%' }} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button size="sm" variant="secondary" icon="play" onClick={() => { void load(); }}>Écouter</Button>
          {attachment.waveform && <Waveform values={attachment.waveform} />}
        </div>
      )}
    </div>
  );
}

function FileTile({ attachment }: { attachment: AttachmentDTO }) {
  const ext = attachment.name.includes('.') ? attachment.name.split('.').pop()!.toUpperCase() : '';
  return (
    <div style={{ ...frame, padding: 10, display: 'flex', alignItems: 'center', gap: 10 }} onClick={(e) => e.stopPropagation()}>
      <span aria-hidden="true" style={{ width: 36, height: 36, flexShrink: 0, borderRadius: 'var(--radius-sm)', background: 'var(--color-primary-muted)', color: 'var(--color-primary-text)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name="file-text" size={18} />
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{attachment.name}</span>
        <span className="pl-msg-meta pl-num">{[ext, formatSize(attachment.size)].filter(Boolean).join(' · ')}</span>
      </span>
      <Button size="sm" variant="ghost" icon="download" aria-label={`Télécharger ${attachment.name}`} title="Télécharger" onClick={() => { void downloadAttachment(attachment); }} />
    </div>
  );
}

export function MessageAttachments({ attachments, dimmed }: { attachments: AttachmentDTO[]; dimmed?: boolean }) {
  if (attachments.length === 0) return null;
  const images = attachments.filter((a) => a.kind === 'image');
  const others = attachments.filter((a) => a.kind !== 'image');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 360, marginTop: 4, opacity: dimmed ? 0.6 : 1 }}>
      {images.length > 0 && (
        <div style={{ display: 'grid', gap: 4, gridTemplateColumns: images.length === 1 ? '1fr' : 'repeat(2, 1fr)' }}>
          {images.map((a) => <ImageTile key={a.id} attachment={a} single={images.length === 1} />)}
        </div>
      )}
      {others.map((a) => (a.kind === 'video' ? <VideoTile key={a.id} attachment={a} />
        : a.kind === 'voice' ? <VoicePlayer key={a.id} load={() => attachmentFileUrl(a.id)} durationMs={a.durationMs} waveform={a.waveform} />
        : a.kind === 'audio' ? <AudioTile key={a.id} attachment={a} />
        : <FileTile key={a.id} attachment={a} />))}
    </div>
  );
}

function DraftChip({ conversationId, draft }: { conversationId: string; draft: DraftFile }) {
  const icon = draft.type.startsWith('video/') ? 'film' : draft.type.startsWith('audio/') ? 'music' : draft.type.startsWith('image/') ? 'image' : 'file-text';
  return (
    <li style={{ ...frame, position: 'relative', display: 'flex', alignItems: 'center', gap: 8, padding: 6, width: 200, flexShrink: 0 }}>
      {draft.localUrl
        ? <img src={draft.localUrl} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 'var(--radius-sm)', flexShrink: 0 }} />
        : <span aria-hidden="true" style={{ width: 36, height: 36, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-secondary)' }}><Icon name={icon} size={18} /></span>}
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
        <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{draft.name}</span>
        {draft.status === 'failed' ? (
          <span role="alert" className="pl-msg-meta" style={{ color: 'var(--color-danger)' }}>
            {uploadErrorLabel(draft.error)}
            {draft.error !== 'FILE_TOO_LARGE' && draft.error !== 'UNSUPPORTED_FILE' && (
              <> <button type="button" style={{ all: 'unset', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => retryDraftFile(conversationId, draft.localId)}>Réessayer</button></>
            )}
          </span>
        ) : draft.status === 'uploading' ? (
          <span role="progressbar" aria-label={`Envoi de ${draft.name}`} aria-valuenow={Math.round(draft.progress * 100)} aria-valuemin={0} aria-valuemax={100}
            style={{ height: 4, borderRadius: 2, background: 'var(--color-border-default)', overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: `${Math.round(draft.progress * 100)}%`, background: 'var(--color-primary)' }} />
          </span>
        ) : (
          <span className="pl-msg-meta pl-num">{formatSize(draft.attachment?.size ?? draft.size)}</span>
        )}
      </span>
      <Button size="sm" variant="ghost" icon="x" aria-label={`Retirer ${draft.name}`} title="Retirer" onClick={() => removeDraftFile(conversationId, draft.localId)} />
    </li>
  );
}

/** Fichiers du message en préparation (au-dessus de la saisie). */
export function DraftFiles({ conversationId }: { conversationId: string }) {
  const drafts = useMessagingStore((s) => s.drafts[conversationId]);
  if (!drafts || drafts.length === 0) return null;
  return (
    <ul aria-label="Fichiers à envoyer" style={{ listStyle: 'none', margin: 0, padding: '8px 16px 0', display: 'flex', gap: 6, overflowX: 'auto' }}>
      {drafts.map((d) => <DraftChip key={d.localId} conversationId={conversationId} draft={d} />)}
    </ul>
  );
}
